import { useCallback, useEffect, useRef } from "react";

import { useLazyGetBoxAccessTokenQuery } from "services/e-documents";

const BOX_TOKEN_TTL_MS = 45 * 60 * 1000;

type BoxTokenProvider = (itemId?: string | string[]) => Promise<string>;

export default function useBoxTokenProvider(
  initialToken?: string | null
): BoxTokenProvider {
  const initialTokenRef = useRef(initialToken);
  const cachedTokenRef = useRef<string | null>(null);
  const cachedAtRef = useRef(0);
  const inFlightRef = useRef<Promise<string> | null>(null);
  const [getBoxAccessToken] = useLazyGetBoxAccessTokenQuery();

  useEffect(() => {
    initialTokenRef.current = initialToken;
  }, [initialToken]);

  return useCallback(async () => {
    const now = Date.now();
    if (cachedTokenRef.current && now - cachedAtRef.current < BOX_TOKEN_TTL_MS) {
      return cachedTokenRef.current;
    }

    if (inFlightRef.current) {
      return inFlightRef.current;
    }

    const tokenPromise = getBoxAccessToken(undefined, false)
      .unwrap()
      .then(({ box_access_token }) => {
        const token = box_access_token || initialTokenRef.current;

        if (!token) {
          throw new Error("Unable to load Box access token");
        }

        cachedTokenRef.current = token;
        cachedAtRef.current = Date.now();

        return token;
      })
      .catch((error) => {
        if (initialTokenRef.current) {
          return initialTokenRef.current;
        }

        throw error;
      })
      .finally(() => {
        inFlightRef.current = null;
      });

    inFlightRef.current = tokenPromise;

    return tokenPromise;
  }, [getBoxAccessToken]);
}
